import { useEffect, useMemo, useRef, useState } from 'react'
import { OmDropdownMenu, OmMenu, OmPopover, OmDialog, OmTooltip, type OmMenuItem } from '@owma/kit'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import FTIcon from 'components/FTIcon'

import DOMPurify from 'dompurify'
import EditResponseComment from '../EditResponseComment'
import AttendeeList from '../AttendeeList'
import Avatar from '../Avatar'
import * as calendarService from '../../modules/CalendarService'
import * as dateUtils from '../../utils/DateUtils'
import { displayEndDate } from '../../utils/eventDateRange'
import { doMeetingResponse } from '../../modules/EventEditProcessor'
import type EventInstance from '../../modules/EventInstance'
import {
  confirmAndDeleteEvent,
  isRecurrenceSeriesStart,
  type EventScope,
} from '../../utils/EventActions'
import { isOrgSharedAccountEmail } from 'utils/orgSharedAccount'
import NoteDetail from '../NoteDetail'
import { rruleDescription } from '../../utils/RRuleDesc'
import closeIcon from 'assets/images/toolbar-close.svg'
import editIcon from 'assets/images/toolbar-edit.svg'
import deleteIcon from 'assets/images/toolbar-delete.svg'
import duplicateIcon from 'assets/images/toolbar-duplicate-24.svg'
import locationIcon from 'assets/images/calendar/location-icon.svg'
import folderIcon from 'assets/images/calendar/calendar-folder-icon.svg'
import busyIcon from 'assets/images/calendar/busy-status-icon.svg'
import noteIcon from 'assets/images/calendar/note-icon.svg'
import googleMeetingIcon from 'assets/images/calendar/google-meeting-icon.svg'
import attendeeIcon from 'assets/images/calendar/property-attendees.svg'
import reminderIcon from 'assets/images/calendar/reminder-icon.svg'
import meetingRsvpIcon from 'assets/images/calendar/property-rsvp.svg'
import attachmentIcon from 'assets/images/calendar/attachment-20.svg'
import mapsIcon from 'assets/images/icon-map-20.svg'
import videoIcon from 'assets/images/icon-video-24.svg'

import styles from './style.module.scss'

type EditType = 'all' | 'future' | 'this' | null
type MeetingStatus = 'ACCEPTED' | 'DECLINED' | 'TENTATIVE'

interface AttendeeParams {
  CN?: string
}

interface Attendee {
  val: string
  params: AttendeeParams
}

interface Author {
  val: string
  params?: AttendeeParams
}

interface Attachment {
  url: string
  filename: string
  mimeType: string
}

interface EventException {
  recurrenceid?: Date
}

interface CalendarEvent {
  subject: string
  startDate: Date
  endDate: Date
  allday: boolean
  location?: string
  description?: string
  color: string
  serverId: string
  isPrivate?: boolean
  isRecurring?: boolean
  hasAttachment?: boolean
  isMeeting?: boolean
  isMyEvent?: boolean
  isCanceled?: boolean
  isDeclined?: boolean
  isContainMe?: boolean
  isBusy?: boolean
  author?: Author
  attendees: Attendee[]
  attachments?: Attachment[]
  alarmsStrings: string[]
  myMeetingResponse?: MeetingStatus
  event: {
    start: Date
    rrule?: any
  }
  exception?: EventException
}

interface Calendar {
  displayName: string
  owner: string
  isReadOnly: boolean
}

interface EventDetailProps {
  event: CalendarEvent | null
  onEdit: (editType: EditType) => void
  onDuplicate: (event: CalendarEvent) => void
  onClose: () => void
  onDidRender: (element: HTMLDivElement) => void
}

const EventDetail = ({ event, onEdit, onDuplicate, onClose, onDidRender }: EventDetailProps) => {
  // 훅을 가진 본문(EventDetailContent)은 event 가 항상 존재한다고 가정한다.
  // Rules of Hooks 상 훅은 매 렌더 동일 순서로 실행돼야 하므로, null 가드는 훅이 없는 이 래퍼에서 처리하고
  // event 가 없으면 본문을 아예 마운트하지 않는다(언마운트 → 훅 개수 감소로 인한 React #300 방지).
  if (!event) return null
  return (
    <EventDetailContent
      event={event}
      onEdit={onEdit}
      onDuplicate={onDuplicate}
      onClose={onClose}
      onDidRender={onDidRender}
    />
  )
}

interface EventDetailContentProps extends Omit<EventDetailProps, 'event'> {
  event: CalendarEvent
}

const EventDetailContent = ({
  event,
  onEdit,
  onDuplicate,
  onClose,
  onDidRender,
}: EventDetailContentProps) => {
  const { t } = useTranslation()

  const me = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (me.current) {
      onDidRender(me.current)
    }
  }, [onDidRender])

  const instance = event as unknown as EventInstance

  const handleEventDelete = (deleteType: EventScope = 'all') => {
    confirmAndDeleteEvent(instance, deleteType, onClose)
  }

  const parentCalendar: Calendar | null = useMemo(
    () => calendarService.findCalendarByEventServerId(event.serverId) as any,
    [event.serverId],
  )

  const handleDuplicateEvent = () => {
    onDuplicate(event)
  }

  const renderToolbar = () => {
    const hideFuture = isRecurrenceSeriesStart(instance)

    const deleteMenuItems: OmMenuItem[] = [
      {
        key: 'this',
        label: t('calendar.edit.thisEvent'),
        onClick: () => handleEventDelete('this'),
      },
      !hideFuture && {
        key: 'future',
        label: t('calendar.edit.thisAndAllFutureEvents'),
        onClick: () => handleEventDelete('future'),
      },
      {
        key: 'all',
        label: t('calendar.edit.allEventsInTheSeries'),
        onClick: () => handleEventDelete('all'),
      },
    ].filter(Boolean) as OmMenuItem[]

    const editMenuItems: OmMenuItem[] = [
      {
        key: 'this',
        label: t('calendar.edit.thisEvent'),
        onClick: () => onEdit('this'),
      },
      !hideFuture && {
        key: 'future',
        label: t('calendar.edit.thisAndAllFutureEvents'),
        onClick: () => onEdit('future'),
      },
      {
        key: 'all',
        label: t('calendar.edit.allEventsInTheSeries'),
        onClick: () => onEdit('all'),
      },
    ].filter(Boolean) as OmMenuItem[]

    const deleteMenu = <OmMenu items={deleteMenuItems} className={classNames(styles.contextMenu)} />
    const editMenu = <OmMenu items={editMenuItems} className={classNames(styles.contextMenu)} />

    return (
      <div className={styles.toolbar}>
        <div className={styles.leftIcons}>
          {event.author && (
            <OmTooltip title={event.author.params?.CN || event.author.val}>
              <div className={styles.authorIcon}>
                <Avatar
                  name={event.author.params?.CN || event.author.val}
                  email={event.author.val}
                />
              </div>
            </OmTooltip>
          )}
        </div>
        <div className={styles.toolbarButtons}>
          <OmTooltip title={t('signature.menu.duplicate')}>
            <button className={styles.duplicateButton} onClick={handleDuplicateEvent}>
              <img src={duplicateIcon} alt="Duplicate" />
            </button>
          </OmTooltip>

          {parentCalendar && !parentCalendar.isReadOnly && (
            <OmTooltip title={t('common.button.edit')}>
              {event.isRecurring ? (
                <OmDropdownMenu
                  popupRender={() => editMenu}
                  trigger={['click']}
                  overlayClassName={classNames('contextMenu')}
                >
                  <button className={styles.editButton}>
                    <img src={editIcon} alt="Edit" />
                  </button>
                </OmDropdownMenu>
              ) : (
                <button className={styles.editButton} onClick={() => onEdit(null)}>
                  <img src={editIcon} alt="Edit" />
                </button>
              )}
            </OmTooltip>
          )}

          {parentCalendar && !parentCalendar.isReadOnly && (
            <OmTooltip title={t('common.button.delete')}>
              {event.isRecurring ? (
                <OmDropdownMenu
                  popupRender={() => deleteMenu}
                  trigger={['click']}
                  overlayClassName={classNames('contextMenu')}
                >
                  <button className={styles.deleteButton}>
                    <img src={deleteIcon} alt="Delete" />
                  </button>
                </OmDropdownMenu>
              ) : (
                <button className={styles.deleteButton} onClick={() => handleEventDelete()}>
                  <img src={deleteIcon} alt="Delete" />
                </button>
              )}
            </OmTooltip>
          )}

          <OmTooltip title={t('common.button.close')}>
            <button className={styles.closeButton} onClick={onClose}>
              <img src={closeIcon} alt="Close" />
            </button>
          </OmTooltip>
        </div>
      </div>
    )
  }

  const renderTitle = () => {
    let subject = event.subject
    if (subject == null || subject.length === 0) {
      subject = t('editor.value.noSubject')
    }

    return (
      <div className={styles.header}>
        <div className={styles.colorDot} style={{ backgroundColor: event.color }} />
        <div className={styles.title}>
          <div>{subject}</div>
        </div>
      </div>
    )
  }

  const renderTime = () => {
    let firstLine = ''
    let secondLine: string | null = null
    if (
      !event.allday &&
      dateUtils.isSameDay(event.startDate, displayEndDate(event.startDate, event.endDate, 1000))
    ) {
      firstLine = dateUtils.localizedDateString_ddddMMMMDYYYY(event.startDate)
      secondLine =
        dateUtils.localizedTimeString(event.startDate) +
        ' → ' +
        dateUtils.localizedTimeString(event.endDate)
    } else if (event.allday) {
      if (
        dateUtils.isSameDay(event.startDate, displayEndDate(event.startDate, event.endDate, 1000))
      ) {
        firstLine =
          dateUtils.localizedDateString_ddddMMMMDYYYY(event.startDate) +
          ' (' +
          t('calendar.edit.allDay') +
          ')'
      } else {
        firstLine = dateUtils.localizedDateString_ddddMMMMDYYYY(event.startDate) + ' →'
        secondLine = dateUtils.localizedDateString_ddddMMMMDYYYY(
          displayEndDate(event.startDate, event.endDate, 1000),
        )
      }
    } else {
      //multi day event.
      firstLine =
        dateUtils.localizedDateString_ddddMMMMDYYYY(event.startDate) +
        ' ' +
        dateUtils.localizedTimeString(event.startDate) +
        ' →'
      secondLine =
        dateUtils.localizedDateString_ddddMMMMDYYYY(event.endDate) +
        ' ' +
        dateUtils.localizedTimeString(event.endDate)
    }

    const rruleStr = event.event.rrule ? rruleDescription(event.event.rrule) : null

    return (
      <div className={styles.time}>
        <p>
          {firstLine}
          <br />
          {secondLine}
          {rruleStr && (
            <>
              <br />
              {rruleStr}
            </>
          )}
        </p>
      </div>
    )
  }

  const renderLocation = () => {
    if (!event.location) return null

    const showVideoIcon =
      event.location.includes('meet.google.com') || event.location.includes('.zoom.us')
    let location = event.location

    if (showVideoIcon && !location.startsWith('http')) {
      location = 'https://' + location
    }

    let l: URL | null = null
    try {
      l = new URL(location)
    } catch {
      console.log('create url failed')
    }

    return (
      <div className={styles.section}>
        <div className={styles.location}>
          <img src={locationIcon} alt="Location" className={styles.locationIcon} />
          {l ? (
            <div className={styles.locationText}>
              <a href={l.href} target="_blank" rel="noopener noreferrer">
                {event.location}
              </a>
              {showVideoIcon && (
                <a href={l.href} target="_blank" rel="noopener noreferrer">
                  <img src={videoIcon} alt="Link" />
                </a>
              )}
            </div>
          ) : (
            <div className={styles.locationText}>
              <span>{location}</span>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={mapsIcon} alt="Google Maps" />
              </a>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderGoogleMeet = () => {
    if (!event.description || !event.description.includes('meet.google.com')) return null
    const meetUrl = event.description.match(/meet\.google\.com\/[a-z-]+/)?.[0]
    if (!meetUrl) return null

    return (
      <div className={styles.section}>
        <div className={styles.googleMeet}>
          <img src={googleMeetingIcon} alt="Google Meet" />
          <div>
            <a href={`https://${meetUrl}`} target="_blank" rel="noopener noreferrer">
              {t('calendar.result.joinGoogleMeet')}
            </a>
            <div style={{ marginTop: '2px' }}>{meetUrl}</div>
          </div>
        </div>
      </div>
    )
  }

  const [attendeesToShow, setAttendeesToShow] = useState<Attendee[]>([])
  const [showMoreAttendees, setShowMoreAttendees] = useState(false)
  const maxAttendees = 8

  useEffect(() => {
    if (!event.attendees.length) return

    let attendeesToDisplay: Attendee[] = []
    if (event.attendees.length > maxAttendees) {
      attendeesToDisplay = event.attendees.slice(0, maxAttendees - 1)
      setShowMoreAttendees(true)
    } else {
      attendeesToDisplay = event.attendees
      setShowMoreAttendees(false)
    }
    setAttendeesToShow(attendeesToDisplay)
  }, [event.attendees])

  const [showAttendeeList, setShowAttendeeList] = useState(false)

  const handleAttendeeListClose = () => {
    setShowAttendeeList(false)
  }

  const renderAttendees = () => {
    if (event.attendees.length === 0) return null

    return (
      <div className={styles.section}>
        <div
          className={styles.attendees}
          onClick={() => setShowAttendeeList(true)}
          style={{ cursor: 'pointer' }}
        >
          <img src={attendeeIcon} alt="Attendees" />
          <div className={styles.attendeesList}>
            {attendeesToShow.map((attendee) => (
              <div key={attendee.val} className={styles.avatar}>
                <Avatar name={attendee.params.CN || attendee.val} email={attendee.val} />
                <div className={styles.tooltip}>
                  {attendee.params.CN ? `${attendee.params.CN}\n` : ''}
                  {attendee.val}
                </div>
              </div>
            ))}
            {showMoreAttendees && (
              <div className={styles.more}>+{event.attendees.length - (maxAttendees - 1)}</div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderAttachments = () => {
    if (!event.attachments || event.attachments.length === 0) return null

    return (
      <div className={styles.section}>
        <div className={styles.attachments}>
          <img src={attachmentIcon} alt="Attachments" />
          <div className={styles.attachmentList}>
            {event.attachments.map((attachment, index) => (
              <div
                key={index}
                className={styles.attachmentCell}
                onClick={() => calendarService.fetchAttachment(attachment.url, attachment.filename)}
              >
                <FTIcon fileName={attachment.filename} fileType={attachment.mimeType} size={24} />
                <div className={styles.attachmentName}>{attachment.filename}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const handleViewMore = () => {
    setShowNoteDetail(true)
  }

  const [showNoteDetail, setShowNoteDetail] = useState(false)
  const noteRef = useRef<HTMLDivElement>(null)
  const [isViewMore, setIsViewMore] = useState(false)

  useEffect(() => {
    const nRef = noteRef.current
    if (nRef) {
      const nRefHeight = nRef.clientHeight
      const nRefScrollHeight = nRef.scrollHeight
      if (nRefHeight < nRefScrollHeight) {
        setIsViewMore(true)
      }
    }
  }, [noteRef])

  const urlify = (text: string): string => {
    if (!text) return ''
    const urlRegex = /(https?:\/\/[^\s]+)/g
    return text.replace(urlRegex, function (url) {
      return '<a href="' + url + '" target="_blank">' + url + '</a>'
    })
  }
  const linkifyNote = urlify(event.description || '')

  const renderNote = () => {
    if (!event.description) return null
    return (
      <div className={styles.section}>
        <div className={styles.note}>
          <img src={noteIcon} alt="note" className={styles.noteIcon} />
          <div
            className={`${styles.textContent} ${isViewMore ? styles.viewMore : ''}`}
            ref={noteRef}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<p>${linkifyNote}</p>`) }}
          />
        </div>
        {isViewMore && (
          <button className={styles.viewMoreButton} onClick={handleViewMore}>
            {t('calendar.detail.viewMore')}
          </button>
        )}
      </div>
    )
  }

  const renderReminders = () => {
    if (event.alarmsStrings.length === 0) return null
    return (
      <div className={styles.section}>
        <div className={styles.reminders}>
          <img src={reminderIcon} alt="Reminders" />
          <div>
            {event.alarmsStrings.map((alarm) => (
              <span key={alarm}>{alarm}</span>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const renderFreeBusy = () => {
    return (
      <div className={styles.section}>
        <div className={styles.busyStatus}>
          <img src={busyIcon} alt="Busy" className={styles.busyStatusIcon} />
          <span>{event.isBusy ? t('calendar.edit.busy') : t('calendar.edit.free')}</span>
        </div>
      </div>
    )
  }

  const renderCalendar = () => {
    if (!parentCalendar) return null

    const isOrgShared = isOrgSharedAccountEmail(parentCalendar.owner)

    return (
      <div className={styles.section}>
        <div className={styles.folder}>
          <img src={folderIcon} alt="Calendar" className={styles.folderIcon} />
          <div>
            <span>{parentCalendar.displayName}</span>
            {!isOrgShared && (
              <>
                <br />
                <span>{parentCalendar.owner}</span>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  const [selectedStatus, setSelectedStatus] = useState<MeetingStatus | null>(null)
  const [selectedType, setSelectedType] = useState<'this' | 'all' | null>(null)
  const [changeMeetingResponse, setChangeMeetingResponse] = useState(false)
  const [showEditResponse, setShowEditResponse] = useState(false)

  // RSVP 버튼 refs — 중첩 함수(renderMeetingResponse) 안에서 호출하면 Rules of Hooks 위반이므로 최상위로 끌어올린다.
  const acceptBtn = useRef<HTMLButtonElement>(null)
  const declineBtn = useRef<HTMLButtonElement>(null)
  const tentativeBtn = useRef<HTMLButtonElement>(null)

  const handleChangeMeetingResponse = (
    status: MeetingStatus,
    type: 'all' | 'this' = 'all',
    sendOption: 'now' | 'edit' = 'now',
  ) => {
    if (sendOption === 'edit') {
      setShowEditResponse(true)
    } else {
      doMeetingResponse(event as any, status, type, null, onClose)

      setSelectedStatus(null)
      setSelectedType(null)
      setChangeMeetingResponse(false)
    }
  }

  const handleEditResponseSubmit = (comment: string, status: MeetingStatus) => {
    doMeetingResponse(event as any, status, selectedType || 'all', comment, onClose)
    handleEditResponseClose()
    setChangeMeetingResponse(false)
  }

  const handleEditResponseClose = () => {
    setShowEditResponse(false)
    setSelectedStatus(null)
    setSelectedType(null)
  }

  const renderSendOptions = (status: MeetingStatus, type: 'this' | 'all') => {
    const items: OmMenuItem[] = [
      {
        key: 'edit',
        label: t('calendar.edit.editBeforeSend'),
        onClick: () => handleChangeMeetingResponse(status, type, 'edit'),
      },
      {
        key: 'send',
        label: t('calendar.edit.sendNow'),
        onClick: () => handleChangeMeetingResponse(status, type, 'now'),
      },
    ]
    return <OmMenu items={items} />
  }

  const renderRecurringOptions = (_status: MeetingStatus) => {
    const items: OmMenuItem[] = [
      {
        key: 'this',
        label: (
          <div
            style={{
              width: '100%',
              cursor: 'pointer',
            }}
            onClick={() => setSelectedType('this')}
          >
            {t('calendar.edit.thisEvent')}
          </div>
        ),
      },
      {
        key: 'all',
        label: (
          <div
            style={{
              cursor: 'pointer',
            }}
            onClick={() => setSelectedType('all')}
          >
            {t('calendar.edit.allEventsInTheSeries')}
          </div>
        ),
      },
    ]
    return <OmMenu items={items} selectable={false} />
  }

  const changeMeetingResponseState = () => {
    setChangeMeetingResponse(true)
    setTimeout(() => {
      if (me.current) {
        onDidRender(me.current)
      }
    }, 200)
  }

  const renderMeetingResponse = () => {
    if (!event.isMeeting || event.isMyEvent || event.isCanceled || !event.isContainMe) return null

    const showResponseButton = !event.myMeetingResponse || changeMeetingResponse
    let statusString = ''
    if (event.myMeetingResponse === 'ACCEPTED') {
      statusString = t('meeting.status.accepted')
    } else if (event.myMeetingResponse === 'DECLINED') {
      statusString = t('meeting.status.declined')
    } else if (event.myMeetingResponse === 'TENTATIVE') {
      statusString = t('meeting.status.tentative')
    }

    const handleResponseClick = (status: MeetingStatus) => {
      setSelectedStatus(status)
      if (!event.isRecurring) {
        setSelectedType('all')
      }
    }

    return (
      <div className={styles.section}>
        <div className={styles.meetingResponse}>
          <SVG src={meetingRsvpIcon} title="Meeting RSVP" />
          <div style={{ marginLeft: '4px' }}>
            <span>{t('calendar.result.RSVP')}</span>
            {showResponseButton && (
              <div className={styles.responseButtons}>
                <button
                  ref={acceptBtn}
                  className={styles.accept}
                  onClick={() => handleResponseClick('ACCEPTED')}
                >
                  {t('common.button.accept')}
                </button>
                <button
                  ref={declineBtn}
                  className={styles.decline}
                  onClick={() => handleResponseClick('DECLINED')}
                >
                  {t('common.button.decline')}
                </button>
                <button
                  ref={tentativeBtn}
                  className={styles.tentative}
                  onClick={() => handleResponseClick('TENTATIVE')}
                >
                  {t('common.button.tentative')}
                </button>
                {selectedStatus && (
                  <div className={styles.dropdownContainer}>
                    {event.isRecurring ? (
                      selectedType ? (
                        <OmPopover
                          content={renderSendOptions(selectedStatus, selectedType)}
                          trigger="click"
                          open={true}
                          getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
                          bodyStyle={{ padding: '4px 0' }}
                          onOpenChange={(open) => {
                            if (!open && !showEditResponse) {
                              setSelectedStatus(null)
                              setSelectedType(null)
                            }
                          }}
                        >
                          <div
                            style={{
                              height: 0,
                              position: 'absolute',
                              top: '-40px',
                              left:
                                selectedStatus === 'ACCEPTED' && acceptBtn.current
                                  ? acceptBtn.current.offsetLeft + acceptBtn.current.offsetWidth / 2
                                  : selectedStatus === 'DECLINED' && declineBtn.current
                                    ? declineBtn.current.offsetLeft +
                                      declineBtn.current.offsetWidth / 2
                                    : tentativeBtn.current
                                      ? tentativeBtn.current.offsetLeft +
                                        tentativeBtn.current.offsetWidth / 2
                                      : 0,
                            }}
                          />
                        </OmPopover>
                      ) : (
                        <OmPopover
                          content={renderRecurringOptions(selectedStatus)}
                          trigger="click"
                          open={true}
                          getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
                          bodyStyle={{ padding: '4px 0' }}
                          onOpenChange={(open) => {
                            if (!open) {
                              setSelectedStatus(null)
                              setSelectedType(null)
                            }
                          }}
                        >
                          <div
                            style={{
                              height: 0,
                              position: 'absolute',
                              top: '-40px',
                              left:
                                selectedStatus === 'ACCEPTED' && acceptBtn.current
                                  ? acceptBtn.current.offsetLeft + acceptBtn.current.offsetWidth / 2
                                  : selectedStatus === 'DECLINED' && declineBtn.current
                                    ? declineBtn.current.offsetLeft +
                                      declineBtn.current.offsetWidth / 2
                                    : tentativeBtn.current
                                      ? tentativeBtn.current.offsetLeft +
                                        tentativeBtn.current.offsetWidth / 2
                                      : 0,
                            }}
                          />
                        </OmPopover>
                      )
                    ) : (
                      <OmPopover
                        content={renderSendOptions(selectedStatus, 'all')}
                        trigger="click"
                        open={true}
                        getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
                        bodyStyle={{ padding: '4px 0' }}
                        onOpenChange={(open) => {
                          if (!open && !showEditResponse) {
                            setSelectedStatus(null)
                            setSelectedType(null)
                          }
                        }}
                      >
                        <div
                          style={{
                            height: 0,
                            position: 'absolute',
                            top: '-40px',
                            left:
                              selectedStatus === 'ACCEPTED' && acceptBtn.current
                                ? acceptBtn.current.offsetLeft + acceptBtn.current.offsetWidth / 2
                                : selectedStatus === 'DECLINED' && declineBtn.current
                                  ? declineBtn.current.offsetLeft +
                                    declineBtn.current.offsetWidth / 2
                                  : tentativeBtn.current
                                    ? tentativeBtn.current.offsetLeft +
                                      tentativeBtn.current.offsetWidth / 2
                                    : 0,
                          }}
                        />
                      </OmPopover>
                    )}
                  </div>
                )}
              </div>
            )}
            {!showResponseButton && (
              <div className={styles.responseStatus}>
                <span>{statusString}</span>
                <button className={styles.changeResponse} onClick={changeMeetingResponseState}>
                  {t('common.button.change')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.eventDetail} ref={me}>
      {renderToolbar()}
      {renderTitle()}
      {renderTime()}
      {renderLocation()}
      {renderGoogleMeet()}
      {renderAttendees()}
      {renderAttachments()}
      {renderNote()}
      {renderReminders()}
      {renderFreeBusy()}
      {renderCalendar()}
      {renderMeetingResponse()}
      <OmDialog
        open={showEditResponse}
        closable={false}
        onClose={handleEditResponseClose}
        width={500}
        destroyOnHidden
        zIndex={2130}
        contentStyle={{ padding: 0, overflow: 'hidden' }}
      >
        <EditResponseComment
          status={selectedStatus || 'ACCEPTED'}
          onSubmit={handleEditResponseSubmit}
          onCancel={handleEditResponseClose}
        />
      </OmDialog>
      <OmDialog
        open={showAttendeeList}
        onClose={handleAttendeeListClose}
        width={400}
        destroyOnHidden
        closable={false}
        zIndex={2130}
        contentStyle={{ padding: 0, overflow: 'hidden' }}
      >
        <AttendeeList instance={event} onClose={handleAttendeeListClose} />
      </OmDialog>
      <OmDialog
        wrapClassName={classNames('noteDetailModal')}
        className={classNames('mobile-fullpage')}
        open={showNoteDetail}
        onClose={() => setShowNoteDetail(false)}
        width={500}
        destroyOnHidden
        closable={false}
        zIndex={2130}
        contentStyle={{ padding: 0, overflow: 'hidden' }}
      >
        <NoteDetail note={event.description || ''} onClose={() => setShowNoteDetail(false)} />
      </OmDialog>
    </div>
  )
}

export default EventDetail
